/**
 * Cloudflare Image Transformation Options.
 *
 * @see https://developers.cloudflare.com/images/transform-images/transform-via-url
 */
interface CloudflareImageTransformationOptions {
    /**
     * Whether to preserve animation frames from input files.
     * Default is `true`. Set to `false` to reduce animations to still images.
     */
    anim?: boolean;

    /**
     * Background color to add underneath the image.
     * Applies to transparent images (e.g., PNG) or images resized with `fit=pad`.
     * Accepts any valid CSS color (e.g., `#RRGGBB`, `red`, `rgb(255 255 0)`).
     */
    background?: string;

    /**
     * Blur radius between 1 (slight blur) and 250 (maximum).
     */
    blur?: number;

    /**
     * Adds a border around the image after resizing.
     * Can specify width and color, or individual widths for each side.
     *
     * @deprecated only available on workers
     */
    border?: never;

    /**
     * Increase brightness by a factor.
     * A value of 1.0 equals no change, 0.5 equals half brightness, 2.0 equals twice as bright.
     */
    brightness?: number;

    /**
     * Reduces latency on a cache miss by selecting the quickest-to-compress file format.
     * Results in larger file sizes and lower quality. Use sparingly.
     */
    compression?: 'fast';

    /**
     * Increase contrast by a factor.
     * A value of 1.0 equals no change, 0.5 equals low contrast, 2.0 equals high contrast.
     */
    contrast?: number;

    /**
     * Device Pixel Ratio. Default is `1`. Multiplier for width/height for high-DPI images.
     */
    dpr?: number;

    /**
     * Resizing mode. Available values: `scale-down`, `contain`, `cover`, `crop`, `pad`.
     */
    fit?: 'scale-down' | 'contain' | 'cover' | 'crop' | 'pad';

    /**
     * Specifies the image format. Options include `auto`, `avif`, `webp`, `jpeg`, `baseline-jpeg`, `json`.
     * `auto` serves WebP or AVIF if supported by the browser.
     */
    format?: 'auto' | 'avif' | 'webp' | 'jpeg' | 'baseline-jpeg' | 'json';

    /**
     * Increase exposure by a factor.
     * A value of 1.0 equals no change, 0.5 darkens, 2.0 lightens the image.
     */
    gamma?: number;

    /**
     * Defines the focal point or side for cropping.
     * Available options: `auto`, `left`, `right`, `top`, `bottom`, or coordinates (`x` and `y`).
     */
    gravity?: 'auto' | 'left' | 'right' | 'top' | 'bottom' | { x: number; y: number };

    /**
     * Maximum height of the image in pixels.
     */
    height?: number;

    /**
     * Controls how much EXIF metadata to preserve.
     * Options: `keep`, `copyright`, `none`.
     */
    metadata?: 'keep' | 'copyright' | 'none';

    /**
     * On fatal error, redirects to the unresized source image URL. Ignored for cross-domain images.
     */
    onerror?: 'redirect';

    /**
     * Specifies quality for JPEG, WebP, and AVIF formats (scale from 1 to 100).
     * Default is 85. Explicit quality setting enables PNG8 variant for PNG format.
     */
    quality?: number;

    /**
     * Rotates the image by 90, 180, or 270 degrees.
     * Width and height refer to axes after rotation.
     */
    rotate?: 90 | 180 | 270;

    /**
     * Specifies strength of sharpening filter.
     * Value is between 0 (no sharpening) and 10 (maximum). 1 is recommended for downscaled images.
     */
    sharpen?: number;

    /**
     * Specifies a number of pixels to trim from each side of the image.
     * Can be provided as a semicolon-separated string (`top;right;bottom;left`) or individual values.
     */
    trim?: string;

    /**
     * Maximum width of the image in pixels.
     */
    width?: number;
}

const defaultOptions = {
    format: 'auto',
    quality: 82,
    metadata: 'none',
    fit: 'scale-down'
} satisfies CloudflareImageTransformationOptions;

export function buildTransformUrl(
    bucketUrl: string,
    imageId: string,
    options: CloudflareImageTransformationOptions = {}
) {
    // https://luxfit-cdn.lewo-media.de/cdn-cgi/image/format=avif,quality=82/vlcsnap-2024-07-31-15h04m44s638.png
    options = {
        ...defaultOptions,
        ...options
    };

    const transformOptions = Object.entries(options)
        .filter(([_, value]) => value !== undefined)
        .map(([key, value]) => `${key}=${value}`)
        .join(',');

    return `${bucketUrl}/cdn-cgi/image/${transformOptions}/${imageId}`;
}
